import { useState, useCallback } from 'react';
import { fetchOwnedNFTokenIDs, isXrplAddress } from '../utils/xrplOwner';

/**
 * Manages wallet-address filtering for a collection page.
 * Fetching is only triggered explicitly (via searchWallet) to respect the
 * XRPL data API's rate limit — never on every keystroke.
 *
 * @returns {{
 *   isWalletSearch: boolean,
 *   ownedIds: Set<string>|null,
 *   walletAddress: string|null,
 *   loading: boolean,
 *   error: string|null,
 *   searchWallet: (address: string) => Promise<void>,
 *   clearWallet: () => void,
 * }}
 */
export default function useWalletFilter() {
  const [ownedIds, setOwnedIds] = useState(null);
  const [walletAddress, setWalletAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchWallet = useCallback(async (address) => {
    const normalized = typeof address === 'string' ? address.trim() : '';
    if (!isXrplAddress(normalized)) {
      setError('Please enter a valid XRPL wallet address.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const ids = await fetchOwnedNFTokenIDs(normalized);
      setOwnedIds(ids);
      setWalletAddress(normalized);
    } catch (err) {
      setError(err.message || 'Could not load wallet NFTs.');
      setOwnedIds(null);
      setWalletAddress(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearWallet = useCallback(() => {
    setOwnedIds(null);
    setWalletAddress(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    isWalletSearch: ownedIds !== null,
    ownedIds,
    walletAddress,
    loading,
    error,
    searchWallet,
    clearWallet,
  };
}
